// =============================================================================
// TestFeedbackPage — list of all tester feedback entries
// =============================================================================
// Shows every CallSession that has tester feedback attached. Each row has
// a "Details" button that opens the existing /calls/:callID page (audio
// player + transcript + logs already wired there).
// =============================================================================

import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { html, nothing } from 'lit';
import { ref } from 'lit/directives/ref.js';
import arrowLeftIcon from '../assets/arrow_left.svg';
import { CallSession } from '../../db/CallSession.js';
import {
  testLabListFeedback,
  testLabSaveFeedback,
  testLabSetFeedbackStatus,
  testLabDeleteFeedback,
} from '../../db/test-lab-resolvers.js';
import { FEEDBACK_STATUSES, getFeedbackStatus } from '../../db/feedback-status.js';
import type { WorkTaskFeedbackLink } from '../../db/work-task-types.js';

type FeedbackTaskActions = {
  onCreateTask?: (source: WorkTaskFeedbackLink) => void;
  onLinkTask?: (source: WorkTaskFeedbackLink) => void;
};

function formatDate(d: Date | string | null | undefined): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'medium' });
  } catch {
    return '—';
  }
}

function truncate(s: string | null | undefined, n: number): string {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

/**
 * Truncate to the first N whitespace-separated tokens. If the input has more
 * than N tokens (or contains newlines), append " …" so the user knows there's
 * more to read in the full-text modal.
 */
function truncateWords(s: string | null | undefined, maxWords: number): string {
  if (!s) return '';
  const collapsed = s.replace(/\s+/g, ' ').trim();
  if (!collapsed) return '';
  const words = collapsed.split(' ');
  if (words.length <= maxWords && s.length === collapsed.length) return collapsed;
  return `${words.slice(0, maxWords).join(' ')} …`;
}

const feedbackID = (entry: CallSession) => entry.sessionID || entry.callUuid || '';

export const TestFeedbackPage = stateful(({ onCreateTask, onLinkTask }: FeedbackTaskActions = {}) => {
  const [testerFilter, setTesterFilter] = useState<string>('');
  // Full-text modal state — which entry's notes are shown in the modal.
  const [viewingEntry, setViewingEntry] = useState<CallSession | null>(null);
  // Edit state — only one row at a time.
  const [editingID, setEditingID] = useState<string | null>(null);
  const [editTester, setEditTester] = useState<string>('');
  const [editNotes, setEditNotes] = useState<string>('');
  const [editSaving, setEditSaving] = useState<boolean>(false);
  const [editError, setEditError] = useState<string | null>(null);
  // Reload nonce — bump to force the query to re-run after a successful save.
  const [reloadNonce, setReloadNonce] = useState<number>(0);
  const [mutation, setMutation] = useState<{ id: string; kind: 'status' | 'delete' } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [deletingEntry, setDeletingEntry] = useState<CallSession | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [actions] = useState({ alive: true, busy: false, reload: 0 });
  useEffect(() => {
    actions.alive = true;
    return () => {
      actions.alive = false;
    };
  }, []);
  const refreshFeedback = () => setReloadNonce(++actions.reload);

  const feedbackQuery = useQuery(
    () => testLabListFeedback(testerFilter, 100)(selectionSet(() => CallSession, 1)),
    [testerFilter, reloadNonce],
  );

  const entries: CallSession[] = (feedbackQuery.value as CallSession[] | undefined) ?? [];
  // Suggestions stay independent of the selected filter. Older names can still be typed.
  const nameSuggestionsQuery = useQuery(
    () => testLabListFeedback('', 200)(selectionSet(() => CallSession, 1)),
    [reloadNonce],
  );
  const nameSuggestions = [
    ...new Set(
      ((nameSuggestionsQuery.value as CallSession[] | undefined) ?? [])
        .map(entry => entry.testerName?.trim())
        .filter((name): name is string => !!name),
    ),
  ].sort((a, b) => a.localeCompare(b, 'de', { sensitivity: 'base' }));

  const startEdit = (s: CallSession) => {
    setEditingID(feedbackID(s) || null);
    setEditTester(s.testerName ?? '');
    setEditNotes(s.testerNotes ?? '');
    setEditError(null);
  };

  const cancelEdit = () => {
    setEditingID(null);
    setEditTester('');
    setEditNotes('');
    setEditError(null);
  };

  const saveEdit = async () => {
    if (!editingID || editSaving || actions.busy) return;
    if (!editTester.trim()) {
      setEditError('Bitte einen Namen eingeben.');
      return;
    }
    if (!editNotes.trim()) {
      setEditError('Notes dürfen nicht leer sein.');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await testLabSaveFeedback(editingID, editTester.trim(), editNotes.trim())();
      setEditingID(null);
      setEditTester('');
      setEditNotes('');
      refreshFeedback();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : String(err));
    } finally {
      setEditSaving(false);
    }
  };

  const changeStatus = async (entry: CallSession, event: Event) => {
    const select = event.currentTarget as HTMLSelectElement;
    const previous = getFeedbackStatus(entry.testerFeedbackStatus);
    const next = getFeedbackStatus(select.value);
    // Native selects change their DOM value first. Keep the confirmed value until the server accepts the change.
    select.value = previous;
    const id = feedbackID(entry);
    if (!id || actions.busy || editSaving || editingID !== null || next === previous) return;
    actions.busy = true;
    setMutation({ id, kind: 'status' });
    setActionError(null);
    try {
      if ((await testLabSetFeedbackStatus(id, next)()) !== true) throw new Error('STATUS_NOT_SAVED');
      if (actions.alive) refreshFeedback();
    } catch (error) {
      if (actions.alive)
        setActionError(
          error instanceof Error && error.message.includes('FEEDBACK_CHANGED')
            ? 'Das Feedback wurde zwischenzeitlich geändert. Bitte laden Sie die Liste neu und prüfen Sie den aktuellen Stand.'
            : 'Der Status konnte nicht gespeichert werden. Der bisherige Status bleibt erhalten.',
        );
    } finally {
      actions.busy = false;
      if (actions.alive) setMutation(null);
    }
  };
  const openDelete = (entry: CallSession) => {
    if (!feedbackID(entry) || actions.busy || editSaving) return;
    setDeleteError(null);
    setDeletingEntry(entry);
  };
  const cancelDelete = () => {
    if (!actions.busy) {
      setDeletingEntry(null);
      setDeleteError(null);
    }
  };
  const deleteFeedback = async () => {
    if (!deletingEntry || actions.busy || editSaving) return;
    const id = feedbackID(deletingEntry);
    if (!id) return;
    actions.busy = true;
    setMutation({ id, kind: 'delete' });
    setDeleteError(null);
    try {
      if ((await testLabDeleteFeedback(id)()) !== true) throw new Error('FEEDBACK_NOT_DELETED');
      if (!actions.alive) return;
      setDeletingEntry(null);
      if (viewingEntry && feedbackID(viewingEntry) === id) setViewingEntry(null);
      if (editingID === id) cancelEdit();
      setActionError(null);
      refreshFeedback();
    } catch (error) {
      if (actions.alive)
        setDeleteError(
          error instanceof Error && error.message.includes('FEEDBACK_CHANGED')
            ? 'Das Feedback wurde zwischenzeitlich geändert. Bitte schließen Sie dieses Fenster und laden Sie die Liste neu.'
            : 'Das Feedback konnte nicht gelöscht werden. Es bleibt erhalten. Bitte erneut versuchen.',
        );
    } finally {
      actions.busy = false;
      if (actions.alive) setMutation(null);
    }
  };
  const renderStatus = (entry: CallSession) => {
    const current = getFeedbackStatus(entry.testerFeedbackStatus);
    const busy = mutation?.kind === 'status' && mutation.id === feedbackID(entry);
    return html`<select
        class="zw-feedback-status"
        data-status=${current}
        aria-label=${'Status für ' + (entry.testerName || 'Feedback')}
        ?disabled=${mutation !== null || editingID !== null || editSaving || !feedbackID(entry)}
        @change=${(event: Event) => void changeStatus(entry, event)}
      >
        ${FEEDBACK_STATUSES.map(
          status => html`<option value=${status.value} .selected=${status.value === current}>${status.label}</option>`,
        )}</select
      >${busy ? html`<span class="zw-feedback-status-saving" role="status">Speichere…</span>` : nothing}`;
  };

  return html`
    <style>
      .zw-feedback {
        max-width: 1480px;
        margin: 0 auto;
        padding: 32px 30px 60px;
        color: #202630;
      }
      .zw-feedback * {
        box-sizing: border-box;
      }
      .zw-feedback :is(h1, h2, h3, p) {
        margin: 0;
      }
      .zw-feedback button,
      .zw-feedback input,
      .zw-feedback textarea {
        font-family: inherit;
      }
      .zw-feedback .zw-feedback-back {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        padding: 0;
        margin-bottom: 26px;
        border: 0;
        background: transparent;
        color: #667085;
        font-size: 12px;
        cursor: pointer;
      }
      .zw-feedback .zw-feedback-back:hover {
        color: #d6001c;
      }
      .zw-feedback .zw-feedback-back img {
        width: 16px;
        height: 16px;
      }
      .zw-feedback .zw-feedback-heading {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 24px;
        margin-bottom: 27px;
      }
      .zw-feedback .zw-feedback-eyebrow {
        color: #d6001c;
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0.14em;
        text-transform: uppercase;
      }
      .zw-feedback h1 {
        margin-top: 8px;
        font-size: clamp(28px, 3vw, 36px);
        line-height: 1.2;
        font-weight: 750;
        letter-spacing: -0.035em;
      }
      .zw-feedback .zw-feedback-intro {
        max-width: 700px;
        margin-top: 11px;
        color: #667085;
        font-size: 13px;
        line-height: 1.8;
      }
      .zw-feedback .zw-feedback-toolbar {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 14px;
        margin-bottom: 18px;
        padding: 17px 20px;
        border: 1px solid #e1e6ed;
        border-radius: 12px;
        background: #fff;
      }
      .zw-feedback .zw-feedback-filter {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px;
      }
      .zw-feedback .zw-feedback-filter label {
        font-size: 12px;
        font-weight: 650;
        color: #475467;
      }
      .zw-feedback .zw-feedback-input {
        width: 270px;
        max-width: 100%;
        min-width: 0;
        height: 41px;
        padding: 9px 12px;
        border: 1px solid #d4dce6;
        border-radius: 8px;
        background: #fff;
        color: #344054;
        font-size: 13px;
      }
      .zw-feedback .zw-feedback-input::placeholder,
      .zw-feedback textarea::placeholder {
        color: #98a2b3;
      }
      .zw-feedback .zw-feedback-count {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-left: auto;
        color: #778397;
        font-size: 12px;
        white-space: nowrap;
      }
      .zw-feedback .zw-feedback-count strong {
        display: inline-flex;
        min-width: 29px;
        height: 27px;
        align-items: center;
        justify-content: center;
        padding: 0 7px;
        border-radius: 7px;
        background: #f1f3f7;
        color: #475467;
        font-size: 12px;
      }
      .zw-feedback .zw-feedback-table-wrap {
        border: 1px solid #e1e6ed;
        border-radius: 14px;
        background: #fff;
        overflow: hidden;
      }
      .zw-feedback .zw-feedback-table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
        font-size: 13px;
      }
      .zw-feedback .zw-feedback-table thead {
        background: #f8f9fb;
        color: #758196;
      }
      .zw-feedback .zw-feedback-table th {
        padding: 15px 18px;
        text-align: left;
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        font-weight: 750;
        border-bottom: 1px solid #e8ecf2;
      }
      .zw-feedback .zw-feedback-table td {
        padding: 24px 18px;
        vertical-align: top;
        border-bottom: 1px solid #edf0f4;
      }
      .zw-feedback .zw-feedback-table tbody tr:last-child td {
        border-bottom: 0;
      }
      .zw-feedback .zw-feedback-row:hover {
        background: #fdfdff;
      }
      .zw-feedback .zw-feedback-date {
        color: #566378;
        font-size: 12px;
        line-height: 1.75;
        font-variant-numeric: tabular-nums;
        overflow-wrap: anywhere;
      }
      .zw-feedback .zw-feedback-name {
        display: inline-flex;
        max-width: 100%;
        align-items: center;
        gap: 7px;
        padding: 5px 9px;
        border-radius: 7px;
        background: #f2f0fa;
        color: #65529a;
        font-size: 12px;
        font-weight: 650;
        line-height: 1.5;
        overflow-wrap: anywhere;
      }
      .zw-feedback .zw-feedback-profile {
        margin-top: 8px;
        color: #8a94a5;
        font-size: 11px;
        line-height: 1.6;
        overflow-wrap: anywhere;
      }
      .zw-feedback .zw-feedback-notes {
        color: #445166;
        font-size: 13px;
        line-height: 1.8;
        overflow-wrap: anywhere;
      }
      .zw-feedback .zw-feedback-notes-preview {
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .zw-feedback .zw-feedback-fulltext {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 0;
        margin-top: 9px;
        border: 0;
        background: transparent;
        color: #c8001c;
        font-size: 11px;
        font-weight: 650;
        cursor: pointer;
      }
      .zw-feedback .zw-feedback-fulltext:hover {
        text-decoration: underline;
      }
      .zw-feedback .zw-feedback-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        min-height: 38px;
        padding: 9px 12px;
        border: 1px solid #d9e0e9;
        border-radius: 8px;
        background: #fff;
        color: #435167;
        font-size: 11px;
        line-height: 1.5;
        font-weight: 650;
        text-decoration: none;
        cursor: pointer;
      }
      .zw-feedback .zw-feedback-button:hover:not(:disabled) {
        border-color: #adb8c8;
        background: #f6f8fa;
      }
      .zw-feedback .zw-feedback-button svg {
        flex-shrink: 0;
      }
      .zw-feedback .zw-feedback-primary {
        color: #fff;
        border-color: #d6001c;
        background: #d6001c;
      }
      .zw-feedback .zw-feedback-primary:hover:not(:disabled) {
        border-color: #b60018;
        background: #b60018;
      }
      .zw-feedback .zw-feedback-quiet {
        padding-left: 7px;
        padding-right: 7px;
        border-color: transparent;
        background: transparent;
        color: #667085;
      }
      .zw-feedback .zw-feedback-button:disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }
      .zw-feedback .zw-feedback-status {
        width: 190px;
        max-width: 100%;
        min-height: 35px;
        padding: 7px 23px 7px 9px;
        border: 1px solid #dce2eb;
        border-radius: 7px;
        color: #667085;
        background: #f5f6f8;
        font-family: inherit;
        font-size: 11px;
        font-weight: 650;
        cursor: pointer;
      }
      .zw-feedback .zw-feedback-status[data-status='pending'] {
        color: #946310;
        border-color: #f1deb8;
        background: #fff9eb;
      }
      .zw-feedback .zw-feedback-status[data-status='in-progress'] {
        color: #2767ab;
        border-color: #caddf3;
        background: #eef5ff;
      }
      .zw-feedback .zw-feedback-status[data-status='ready-for-review'] {
        color: #7752aa;
        border-color: #dfd3f1;
        background: #f6f0ff;
      }
      .zw-feedback .zw-feedback-status[data-status='completed'] {
        color: #28754e;
        border-color: #c7e2d0;
        background: #eff9f2;
      }
      .zw-feedback .zw-feedback-status:disabled {
        opacity: 0.65;
        cursor: wait;
      }
      .zw-feedback .zw-feedback-status-saving {
        display: block;
        margin-top: 7px;
        color: #8792a3;
        font-size: 10px;
      }
      .zw-feedback .zw-feedback-status-label {
        display: none;
      }
      .zw-feedback .zw-feedback-delete {
        color: #a9364a;
        margin-top: 5px;
      }
      .zw-feedback .zw-feedback-delete:hover:not(:disabled) {
        color: #bd1834;
        background: #fff3f5;
        border-color: #f0c4cd;
      }
      .zw-feedback .zw-feedback-actions-error {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 16px;
        padding: 14px 17px;
        border: 1px solid #f0c4cd;
        border-radius: 10px;
        background: #fff7f8;
        color: #a22b40;
        font-size: 12px;
        line-height: 1.7;
      }
      .zw-feedback .zw-feedback-delete-dialog {
        max-width: 560px;
      }
      .zw-feedback .zw-feedback-delete-preview {
        padding: 14px 16px;
        border: 1px solid #e7eaf0;
        border-radius: 9px;
        background: #f8f9fb;
        margin-bottom: 17px;
        white-space: normal;
      }
      .zw-feedback .zw-feedback-delete-preview strong {
        display: block;
        color: #536176;
        font-size: 12px;
        margin-bottom: 7px;
      }
      .zw-feedback .zw-feedback-delete-preview p {
        font-size: 12px;
        line-height: 1.8;
        overflow-wrap: anywhere;
      }
      .zw-feedback .zw-feedback-delete-dialog .zw-feedback-dialog-body {
        white-space: normal;
        font-size: 13px;
      }
      .zw-feedback .zw-feedback-task-actions {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 7px;
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px solid #edf0f4;
      }
      .zw-feedback .zw-feedback-task-actions .zw-feedback-button {
        padding: 7px 8px;
        min-height: 32px;
        font-size: 10px;
        text-align: left;
      }
      .zw-feedback .zw-feedback-task-actions .zw-feedback-fulltext {
        margin-top: 0;
        font-size: 10px;
        text-align: left;
      }
      .zw-feedback .zw-feedback-task-actions button:disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }
      .zw-feedback .zw-feedback-call-id {
        display: block;
        margin-top: 8px;
        color: #98a2b3;
        font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
        font-size: 10px;
        line-height: 1.7;
        overflow-wrap: anywhere;
      }
      .zw-feedback .zw-feedback-tip {
        margin-top: 17px;
        color: #8a94a5;
        font-size: 11px;
        line-height: 1.85;
      }
      .zw-feedback .zw-feedback-tip a {
        color: #68768b;
        text-decoration: underline;
        text-underline-offset: 3px;
      }
      .zw-feedback .zw-feedback-empty {
        padding: 48px 24px;
        border: 1px dashed #d6dde7;
        border-radius: 14px;
        text-align: center;
        color: #7c8798;
        font-size: 13px;
        line-height: 1.85;
      }
      .zw-feedback .zw-feedback-loading {
        padding: 28px 4px;
        color: #8590a2;
        font-size: 13px;
      }
      .zw-feedback .zw-feedback-editor-row td {
        padding: 0;
      }
      .zw-feedback .zw-feedback-editor {
        padding: 24px;
        background: #fafbfe;
      }
      .zw-feedback .zw-feedback-editor-head {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 15px;
        margin-bottom: 22px;
      }
      .zw-feedback .zw-feedback-editor-head h3 {
        color: #354256;
        font-size: 15px;
        font-weight: 700;
      }
      .zw-feedback .zw-feedback-editor-meta {
        margin-top: 5px;
        color: #8691a2;
        font-size: 11px;
        line-height: 1.7;
        overflow-wrap: anywhere;
      }
      .zw-feedback .zw-feedback-form {
        display: grid;
        grid-template-columns: minmax(170px, 1fr) minmax(0, 3fr);
        gap: 20px;
      }
      .zw-feedback .zw-feedback-field {
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-width: 0;
      }
      .zw-feedback .zw-feedback-field label {
        color: #526078;
        font-size: 11px;
        font-weight: 650;
      }
      .zw-feedback .zw-feedback-field input {
        width: 100%;
      }
      .zw-feedback .zw-feedback-textarea {
        display: block;
        width: 100%;
        min-height: 130px;
        padding: 12px 14px;
        border: 1px solid #d4dce6;
        border-radius: 8px;
        resize: vertical;
        background: #fff;
        color: #344054;
        font-size: 13px;
        line-height: 1.8;
      }
      .zw-feedback .zw-feedback-editor-actions {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 9px;
        margin-top: 17px;
      }
      .zw-feedback .zw-feedback-error {
        color: #b01f38;
        font-size: 12px;
        line-height: 1.6;
        margin-top: 8px;
      }
      .zw-feedback :is(button, a, input, textarea, select):focus-visible {
        outline: 3px solid #ecc3cb;
        outline-offset: 3px;
      }
      .zw-feedback .zw-feedback-backdrop {
        position: fixed;
        inset: 0;
        z-index: 100;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px;
        background: #1720338c;
        backdrop-filter: blur(3px);
      }
      .zw-feedback .zw-feedback-dialog {
        width: 760px;
        max-width: 100%;
        max-height: 85vh;
        display: flex;
        flex-direction: column;
        border: 1px solid #e5e9f0;
        border-radius: 17px;
        background: #fff;
        box-shadow: 0 24px 90px #1018283d;
        overflow: hidden;
      }
      .zw-feedback .zw-feedback-dialog-head {
        display: flex;
        align-items: flex-start;
        gap: 20px;
        padding: 25px 28px 21px;
        border-bottom: 1px solid #ecf0f5;
      }
      .zw-feedback .zw-feedback-dialog-head > div {
        flex: 1;
        min-width: 0;
      }
      .zw-feedback .zw-feedback-dialog-head h2 {
        margin-top: 7px;
        font-size: 22px;
        font-weight: 750;
        letter-spacing: -0.025em;
      }
      .zw-feedback .zw-feedback-dialog-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 6px 17px;
        margin-top: 13px;
        color: #8490a1;
        font-size: 11px;
        line-height: 1.7;
      }
      .zw-feedback .zw-feedback-dialog-close {
        min-height: 32px;
        width: 32px;
        padding: 0;
        font-size: 17px;
      }
      .zw-feedback .zw-feedback-dialog-body {
        flex: 1;
        min-height: 0;
        padding: 26px 28px;
        overflow-y: auto;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
        color: #445166;
        font-size: 14px;
        line-height: 1.95;
      }
      .zw-feedback .zw-feedback-dialog-footer {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        padding: 18px 28px;
        border-top: 1px solid #ecf0f5;
      }
      @media (max-width: 1050px) {
        .zw-feedback .zw-feedback-table-wrap {
          border: 0;
          border-radius: 0;
          background: transparent;
          overflow: visible;
        }
        .zw-feedback .zw-feedback-table,
        .zw-feedback .zw-feedback-table tbody {
          display: block;
          width: 100%;
        }
        .zw-feedback .zw-feedback-table colgroup,
        .zw-feedback .zw-feedback-table thead {
          display: none;
        }
        .zw-feedback .zw-feedback-table tr {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          gap: 18px 22px;
          margin-bottom: 14px;
          padding: 22px;
          border: 1px solid #e1e6ed;
          border-radius: 13px;
          background: #fff;
        }
        .zw-feedback .zw-feedback-table td {
          display: block;
          width: auto;
          padding: 0;
          border: 0;
        }
        .zw-feedback .zw-feedback-cell-date {
          grid-column: 2;
          grid-row: 1;
          text-align: right;
        }
        .zw-feedback .zw-feedback-cell-person {
          grid-column: 1;
          grid-row: 1;
        }
        .zw-feedback .zw-feedback-cell-status {
          grid-column: 1 / -1;
          grid-row: 2;
        }
        .zw-feedback .zw-feedback-status-label {
          display: block;
          margin-bottom: 7px;
          color: #8a94a5;
          font-size: 10px;
        }
        .zw-feedback .zw-feedback-cell-notes {
          grid-column: 1 / -1;
          grid-row: 3;
        }
        .zw-feedback .zw-feedback-cell-call {
          grid-column: 1;
          grid-row: 4;
        }
        .zw-feedback .zw-feedback-cell-action {
          grid-column: 2;
          grid-row: 4;
          text-align: right;
        }
        .zw-feedback .zw-feedback-table .zw-feedback-editor-row {
          display: block;
          padding: 0;
          background: #fafbfe;
          overflow: hidden;
        }
        .zw-feedback .zw-feedback-notes-preview {
          -webkit-line-clamp: 3;
        }
      }
      @media (max-width: 640px) {
        .zw-feedback {
          padding: 24px 16px 42px;
        }
        .zw-feedback .zw-feedback-back {
          margin-bottom: 22px;
        }
        .zw-feedback .zw-feedback-heading {
          margin-bottom: 22px;
        }
        .zw-feedback .zw-feedback-toolbar {
          padding: 16px;
          gap: 14px;
        }
        .zw-feedback .zw-feedback-filter {
          width: 100%;
          gap: 9px;
        }
        .zw-feedback .zw-feedback-input {
          width: 100%;
        }
        .zw-feedback .zw-feedback-count {
          margin-left: 0;
        }
        .zw-feedback .zw-feedback-table tr {
          padding: 18px;
          gap: 17px 12px;
        }
        .zw-feedback .zw-feedback-date {
          font-size: 11px;
        }
        .zw-feedback .zw-feedback-editor {
          padding: 18px;
        }
        .zw-feedback .zw-feedback-form {
          grid-template-columns: 1fr;
          gap: 17px;
        }
        .zw-feedback .zw-feedback-editor-head {
          flex-direction: column;
        }
        .zw-feedback .zw-feedback-editor-actions {
          flex-wrap: wrap;
          justify-content: flex-start;
        }
        .zw-feedback .zw-feedback-backdrop {
          padding: 14px;
        }
        .zw-feedback .zw-feedback-dialog {
          max-height: 90vh;
        }
        .zw-feedback .zw-feedback-dialog-head,
        .zw-feedback .zw-feedback-dialog-body,
        .zw-feedback .zw-feedback-dialog-footer {
          padding: 20px;
        }
      }
    </style>
    <section class="zw-feedback">
      <button type="button" class="zw-feedback-back" @click=${() => goTo('/test-lab')}>
        <img src=${arrowLeftIcon} alt="" draggable="false" />Zurück zu Settings
      </button>
      <div class="zw-feedback-heading">
        <div>
          <p class="zw-feedback-eyebrow">Settings · Beobachtungen</p>
          <h1>Tester Feedback</h1>
          <p class="zw-feedback-intro">
            Alle Rückmeldungen zu Ihren Test-Anrufen an einem Ort. Öffnen Sie Details & Audio, um die Aufnahme anzuhören
            und das Transkript zu lesen.
          </p>
        </div>
      </div>
      <div class="zw-feedback-toolbar">
        <div class="zw-feedback-filter">
          <label for="feedback-name-filter">Name filtern</label>
          <input
            id="feedback-name-filter"
            type="search"
            class="zw-feedback-input"
            placeholder="Alle Namen"
            list="feedback-name-suggestions"
            maxlength="100"
            .value=${testerFilter}
            @change=${(e: Event) => setTesterFilter((e.target as HTMLInputElement).value.trim())}
          />
          <datalist id="feedback-name-suggestions">
            ${nameSuggestions.map(name => html`<option value=${name}></option>`)}
          </datalist>
          ${testerFilter
            ? html`<button
                type="button"
                class="zw-feedback-button zw-feedback-quiet"
                @click=${() => setTesterFilter('')}
              >
                Alle anzeigen
              </button>`
            : nothing}
        </div>
        <span class="zw-feedback-count"
          ><strong>${entries.length}</strong> ${entries.length === 1 ? 'Eintrag' : 'Einträge'}</span
        >
      </div>
      ${actionError
        ? html`<div class="zw-feedback-actions-error" role="alert">
            <span>${actionError}</span
            ><button
              type="button"
              class="zw-feedback-button"
              ?disabled=${mutation !== null}
              @click=${() => {
                setActionError(null);
                refreshFeedback();
              }}
            >
              Liste neu laden
            </button>
          </div>`
        : nothing}
      ${feedbackQuery.loading
        ? html`<p class="zw-feedback-loading" role="status">Feedback wird geladen…</p>`
        : entries.length === 0
          ? html`<div class="zw-feedback-empty">
              Noch kein Feedback. Hinterlassen Sie Ihre Beobachtungen nach einem Anruf oder in den Anrufdetails.
            </div>`
          : html`
              <div class="zw-feedback-table-wrap">
                <table class="zw-feedback-table" aria-label="Feedback zu Test-Anrufen">
                  <colgroup>
                    <col style="width: 125px" />
                    <col style="width: 135px" />
                    <col />
                    <col style="width: 226px" />
                    <col style="width: 176px" />
                    <col style="width: 124px" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th scope="col">Datum</th>
                      <th scope="col">Name / Profil</th>
                      <th scope="col">Beobachtungen</th>
                      <th scope="col">Status</th>
                      <th scope="col">Anruf</th>
                      <th scope="col">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${entries.map(s => {
                      const tester = s.testerName ?? '—';
                      const isEditing = editingID !== null && editingID === feedbackID(s);
                      const notes = s.testerNotes ?? '';
                      // Call routes, recordings and logs all use the engine's sessionID.
                      const linkID = feedbackID(s);
                      if (isEditing)
                        return html` <tr class="zw-feedback-editor-row">
                          <td colspan="6">
                            <div class="zw-feedback-editor">
                              <div class="zw-feedback-editor-head">
                                <div>
                                  <h3>Feedback bearbeiten</h3>
                                  <p class="zw-feedback-editor-meta">
                                    ${formatDate(s.testerNotesUpdatedAt ?? s.endedAt ?? s.startedAt)} · Profil:
                                    ${s.testCallerProfile ?? '—'}
                                  </p>
                                </div>
                                ${linkID
                                  ? html`<a class="zw-feedback-button" href=${`/calls/${linkID}`}>Details & Audio ↗</a>`
                                  : nothing}
                              </div>
                              <div class="zw-feedback-form">
                                <div class="zw-feedback-field">
                                  <label for="feedback-edit-name">Name</label>
                                  <input
                                    id="feedback-edit-name"
                                    type="text"
                                    class="zw-feedback-input"
                                    aria-label="Name"
                                    placeholder="Ihr Name"
                                    autocomplete="name"
                                    maxlength="100"
                                    .value=${editTester}
                                    @input=${(e: Event) => setEditTester((e.target as HTMLInputElement).value)}
                                    ?disabled=${editSaving}
                                  />
                                </div>
                                <div class="zw-feedback-field">
                                  <label for="feedback-edit-notes">Beobachtungen</label>
                                  <textarea
                                    id="feedback-edit-notes"
                                    class="zw-feedback-textarea"
                                    rows="4"
                                    .value=${editNotes}
                                    @input=${(e: Event) => setEditNotes((e.target as HTMLTextAreaElement).value)}
                                    ?disabled=${editSaving}
                                  ></textarea>
                                  ${editError
                                    ? html`<p class="zw-feedback-error" role="alert">${editError}</p>`
                                    : nothing}
                                </div>
                              </div>
                              <div class="zw-feedback-editor-actions">
                                <button
                                  type="button"
                                  class="zw-feedback-button zw-feedback-quiet zw-feedback-delete"
                                  @click=${() => openDelete(s)}
                                  ?disabled=${editSaving || mutation !== null}
                                >
                                  Feedback löschen
                                </button>
                                <button
                                  type="button"
                                  class="zw-feedback-button zw-feedback-primary"
                                  @click=${saveEdit}
                                  ?disabled=${editSaving || !editTester.trim() || !editNotes.trim()}
                                >
                                  ${editSaving ? 'Speichere…' : 'Speichern'}
                                </button>
                                <button
                                  type="button"
                                  class="zw-feedback-button"
                                  @click=${cancelEdit}
                                  ?disabled=${editSaving}
                                >
                                  Abbrechen
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>`;
                      return html` <tr class="zw-feedback-row">
                        <td class="zw-feedback-cell-date">
                          <span class="zw-feedback-date"
                            >${formatDate(s.testerNotesUpdatedAt ?? s.endedAt ?? s.startedAt)}</span
                          >
                        </td>
                        <td class="zw-feedback-cell-person">
                          <span class="zw-feedback-name">${tester}</span>
                          <p class="zw-feedback-profile">Profil: ${s.testCallerProfile ?? '—'}</p>
                        </td>
                        <td class="zw-feedback-cell-notes">
                          <p class="zw-feedback-notes zw-feedback-notes-preview">${truncateWords(notes, 36) || '—'}</p>
                          ${notes
                            ? html`<button
                                type="button"
                                class="zw-feedback-fulltext"
                                @click=${() => setViewingEntry(s)}
                                title="Beobachtung vollständig lesen"
                              >
                                Volltext anzeigen <span aria-hidden="true">↗</span>
                              </button>`
                            : nothing}
                        </td>
                        <td class="zw-feedback-cell-status">
                          <span class="zw-feedback-status-label">Status</span>${renderStatus(s)}
                        </td>
                        <td class="zw-feedback-cell-call">
                          ${linkID
                            ? html`<a
                                  class="zw-feedback-button"
                                  href=${`/calls/${linkID}`}
                                  title="Aufnahme und Transkript öffnen"
                                >
                                  <svg
                                    width="13"
                                    height="13"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                  >
                                    <path
                                      d="M6 3.8a.8.8 0 0 1 1.2-.7l10 6.2a.8.8 0 0 1 0 1.4l-10 6.2A.8.8 0 0 1 6 16.2Z"
                                    ></path>
                                  </svg>
                                  Details & Audio </a
                                ><span class="zw-feedback-call-id" title=${linkID}>${truncate(linkID, 28)}</span>`
                            : html`<span class="zw-feedback-call-id">—</span>`}
                        </td>
                        <td class="zw-feedback-cell-action">
                          <button
                            type="button"
                            class="zw-feedback-button zw-feedback-quiet"
                            @click=${() => startEdit(s)}
                            ?disabled=${editingID !== null || mutation !== null || !linkID}
                            title="Name und Beobachtungen bearbeiten"
                          >
                            <svg
                              width="13"
                              height="13"
                              viewBox="0 0 20 20"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="1.5"
                              aria-hidden="true"
                            >
                              <path d="m12 4 4 4M4 12l9-9a2.8 2.8 0 0 1 4 4l-9 9-5 1 1-5Z"></path></svg
                            >Bearbeiten
                          </button>
                          <button
                            type="button"
                            class="zw-feedback-button zw-feedback-quiet zw-feedback-delete"
                            @click=${() => openDelete(s)}
                            ?disabled=${editSaving || mutation !== null || !linkID}
                            aria-label=${'Feedback von ' + tester + ' löschen'}
                          >
                            Löschen
                          </button>
                          ${onCreateTask || onLinkTask
                            ? html`<div class="zw-feedback-task-actions">
                                ${onCreateTask
                                  ? html`<button
                                      type="button"
                                      class="zw-feedback-button"
                                      ?disabled=${editingID !== null || editSaving || mutation !== null || !linkID}
                                      @click=${() =>
                                        onCreateTask({ callID: linkID, testerName: tester, notesPreview: notes })}
                                    >
                                      + Task erstellen
                                    </button>`
                                  : nothing}
                                ${onLinkTask
                                  ? html`<button
                                      type="button"
                                      class="zw-feedback-fulltext"
                                      ?disabled=${editingID !== null || editSaving || mutation !== null || !linkID}
                                      @click=${() =>
                                        onLinkTask({ callID: linkID, testerName: tester, notesPreview: notes })}
                                    >
                                      Mit Task verknüpfen ↗
                                    </button>`
                                  : nothing}
                              </div>`
                            : nothing}
                        </td>
                      </tr>`;
                    })}
                  </tbody>
                </table>
              </div>
              <p class="zw-feedback-tip">
                Test-Anrufe finden Sie auch in der <a href="/calls">Anruf-Übersicht</a>. Über „Bearbeiten“ können Sie
                Name und Beobachtungen aktualisieren.
              </p>
            `}
      ${deletingEntry
        ? html`
            <div
              class="zw-feedback-backdrop"
              @click=${(event: MouseEvent) => {
                if (event.target === event.currentTarget) cancelDelete();
              }}
            >
              <div
                class="zw-feedback-dialog zw-feedback-delete-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="feedback-delete-title"
                @keydown=${(event: KeyboardEvent) => {
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    cancelDelete();
                  }
                  if (event.key !== 'Tab') return;
                  const controls = [
                    ...(event.currentTarget as HTMLElement).querySelectorAll<HTMLButtonElement>(
                      'button:not([disabled])',
                    ),
                  ];
                  if (!controls.length) {
                    event.preventDefault();
                    return;
                  }
                  const first = controls[0],
                    last = controls[controls.length - 1];
                  if (event.shiftKey && event.target === first) {
                    event.preventDefault();
                    last?.focus();
                  } else if (!event.shiftKey && event.target === last) {
                    event.preventDefault();
                    first?.focus();
                  }
                }}
              >
                <header class="zw-feedback-dialog-head">
                  <div>
                    <p class="zw-feedback-eyebrow">Feedback entfernen</p>
                    <h2 id="feedback-delete-title">Feedback wirklich löschen?</h2>
                  </div>
                </header>
                <div class="zw-feedback-dialog-body">
                  <div class="zw-feedback-delete-preview">
                    <strong>${deletingEntry.testerName || 'Ohne Namen'}</strong>
                    <p>${truncateWords(deletingEntry.testerNotes, 28)}</p>
                  </div>
                  <p>
                    Nur dieses Feedback einschließlich seines Status wird entfernt. Der Anruf, das Transkript und die
                    Audioaufnahme bleiben erhalten.
                  </p>
                  ${deleteError ? html`<p class="zw-feedback-error" role="alert">${deleteError}</p>` : nothing}
                </div>
                <footer class="zw-feedback-dialog-footer">
                  <button
                    type="button"
                    class="zw-feedback-button"
                    ${ref(element => {
                      if (element && !actions.busy) queueMicrotask(() => (element as HTMLElement).focus());
                    })}
                    @click=${cancelDelete}
                    ?disabled=${mutation !== null}
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    class="zw-feedback-button zw-feedback-primary"
                    @click=${deleteFeedback}
                    ?disabled=${mutation !== null}
                  >
                    ${mutation?.kind === 'delete' ? 'Lösche…' : 'Feedback löschen'}
                  </button>
                </footer>
              </div>
            </div>
          `
        : nothing}
      ${viewingEntry
        ? html`
            <div
              class="zw-feedback-backdrop"
              @click=${(e: MouseEvent) => {
                if (e.target === e.currentTarget) setViewingEntry(null);
              }}
            >
              <div
                class="zw-feedback-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="feedback-dialog-title"
                @keydown=${(e: KeyboardEvent) => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setViewingEntry(null);
                  }
                  if (e.key !== 'Tab') return;
                  const controls = [
                    ...(e.currentTarget as HTMLElement).querySelectorAll<HTMLElement>(
                      'button:not([disabled]), a[href]',
                    ),
                  ];
                  const first = controls[0];
                  const last = controls[controls.length - 1];
                  if (e.shiftKey && e.target === first) {
                    e.preventDefault();
                    last?.focus();
                  } else if (!e.shiftKey && e.target === last) {
                    e.preventDefault();
                    first?.focus();
                  }
                }}
              >
                <header class="zw-feedback-dialog-head">
                  <div>
                    <p class="zw-feedback-eyebrow">Beobachtung</p>
                    <h2 id="feedback-dialog-title">Feedback im Volltext</h2>
                    <div class="zw-feedback-dialog-meta">
                      <span>${viewingEntry.testerName ?? '—'}</span
                      ><span
                        >${formatDate(
                          viewingEntry.testerNotesUpdatedAt ?? viewingEntry.endedAt ?? viewingEntry.startedAt,
                        )}</span
                      ><span>Profil: ${viewingEntry.testCallerProfile ?? '—'}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    class="zw-feedback-button zw-feedback-quiet zw-feedback-dialog-close"
                    ${ref(element => {
                      if (element) queueMicrotask(() => (element as HTMLElement).focus());
                    })}
                    @click=${() => setViewingEntry(null)}
                    title="Schließen (Esc)"
                    aria-label="Schließen"
                  >
                    ×
                  </button>
                </header>
                <div class="zw-feedback-dialog-body">${viewingEntry.testerNotes ?? ''}</div>
                <footer class="zw-feedback-dialog-footer">
                  ${viewingEntry.sessionID
                    ? html`<a class="zw-feedback-button" href=${`/calls/${viewingEntry.sessionID}`}
                        >Details & Audio ↗</a
                      >`
                    : html`<span></span>`}
                  <button
                    type="button"
                    class="zw-feedback-button zw-feedback-primary"
                    @click=${() => setViewingEntry(null)}
                  >
                    Schließen
                  </button>
                </footer>
              </div>
            </div>
          `
        : nothing}
    </section>
  `;
});
